import {
  FormEvent, InputHTMLAttributes, ReactNode, useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import "./access-ui.css";

export interface AccessUser {
  schema_version: "fixed-point-user/v1";
  id: string;
  username: string;
  role: "user" | "admin";
  priority: number;
  enabled: boolean;
  must_change_password: boolean;
}

interface AccessSession {
  schema_version: "fixed-point-access-session/v1";
  authenticated: boolean;
  csrf_token?: string;
  user?: AccessUser;
}

interface ViewingState {
  schema_version: "fixed-point-viewing-state/v1";
  state: "idle" | "waiting" | "active";
  viewer_id: string;
  max_viewers: number;
  heartbeat_interval_ms: number;
  reason?: "congestion";
  queue_position?: number;
  lease_id?: string;
}

const VIEWER_ID_KEY = "fixed-point-managed-viewer-id";
const PAUSED_VIEWING_RELEASE_DELAY_MS = 10_000;
const MEDIA_PREPARATION_GRACE_MS = 60_000;
const originalFetch = window.fetch.bind(window);
let managedAccess = false;
let managedViewingState: ViewingState["state"] = "idle";
let viewingAccessRequester: (() => Promise<boolean>) | null = null;
const VIEWING_ACCESS_STATE_EVENT = "fixed-point-viewing-access-state";

function publishViewingState(state: ViewingState["state"]): void {
  managedViewingState = state;
  window.dispatchEvent(new CustomEvent(VIEWING_ACCESS_STATE_EVENT, { detail: { state } }));
}

export function useViewingAccessActive(): boolean {
  const current = () => !managedAccess || managedViewingState === "active";
  const [active, setActive] = useState(current);
  useEffect(() => {
    const changed = () => setActive(current());
    window.addEventListener(VIEWING_ACCESS_STATE_EVENT, changed);
    changed();
    return () => window.removeEventListener(VIEWING_ACCESS_STATE_EVENT, changed);
  }, []);
  return active;
}

export async function requestViewingAccess(): Promise<boolean> {
  if (!managedAccess) return true;
  return viewingAccessRequester ? viewingAccessRequester() : managedViewingState === "active";
}

function PasswordInput(props: Omit<InputHTMLAttributes<HTMLInputElement>, "type">) {
  const [visible, setVisible] = useState(false);
  const toggleLabel = visible ? "パスワードを非表示にする" : "パスワードを表示する";
  return <span className="password-field">
    <input {...props} type={visible ? "text" : "password"} />
    <button
      type="button"
      className="password-visibility-toggle"
      aria-label={toggleLabel}
      aria-pressed={visible}
      title={toggleLabel}
      onClick={() => setVisible(value => !value)}
    >
      {visible ? <svg aria-hidden="true" viewBox="0 0 24 24">
        <path d="M3 3l18 18M10.6 10.7a2 2 0 002.7 2.7M9.9 4.2A10.8 10.8 0 0112 4c5.5 0 9 5.2 9 5.2a15.5 15.5 0 01-2.3 2.7M6.6 6.6A16.7 16.7 0 003 9.2S6.5 16 12 16a9.9 9.9 0 004.1-.9" />
      </svg> : <svg aria-hidden="true" viewBox="0 0 24 24">
        <path d="M3 9.2S6.5 4 12 4s9 5.2 9 5.2S17.5 16 12 16 3 9.2 3 9.2z" /><circle cx="12" cy="10" r="2.5" />
      </svg>}
    </button>
  </span>;
}

export function viewerId(): string {
  let value = window.sessionStorage.getItem(VIEWER_ID_KEY);
  if (!value) {
    value = globalThis.crypto.randomUUID();
    window.sessionStorage.setItem(VIEWER_ID_KEY, value);
  }
  return value;
}

function requestPath(input: RequestInfo | URL): string | null {
  try {
    const value = input instanceof Request ? input.url : String(input);
    const url = new URL(value, window.location.origin);
    return url.origin === window.location.origin ? url.pathname : null;
  } catch {
    return null;
  }
}

function protectedReplayPath(path: string): boolean {
  return path.startsWith("/api/race/") || path.startsWith("/api/network/")
    || path === "/api/topology" || path.startsWith("/api/conversation/");
}

export function installManagedFetch(): void {
  window.fetch = (input: RequestInfo | URL, init: RequestInit = {}) => {
    const path = requestPath(input);
    if (!managedAccess || !path || !protectedReplayPath(path)) return originalFetch(input, init);
    const headers = new Headers(input instanceof Request ? input.headers : undefined);
    new Headers(init.headers).forEach((value, key) => headers.set(key, value));
    headers.set("X-Viewer-ID", viewerId());
    return originalFetch(input, { ...init, headers });
  };
}

export function mediaUrl(path: string): string {
  if (!managedAccess) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}viewer_id=${encodeURIComponent(viewerId())}`;
}

export function hlsMediaUrl(releaseId: string, carId: string): string {
  const viewer = managedAccess ? viewerId() : "open";
  return `/hls/${encodeURIComponent(viewer)}/${encodeURIComponent(releaseId)}/${encodeURIComponent(carId)}/index.m3u8`;
}

async function jsonOrProblem(response: Response): Promise<Record<string, unknown>> {
  const value = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok) throw new Error(String(value.title ?? `HTTP ${response.status}`));
  return value;
}

function Login({ onLogin }: { onLogin: (session: AccessSession) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await originalFetch("/api/auth/login", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const value = await jsonOrProblem(response) as unknown as AccessSession;
      setPassword("");
      onLogin(value);
    } catch (reason) {
      setError(String(reason));
    } finally {
      setBusy(false);
    }
  };
  return <main className="access-page">
    <form className="access-card" onSubmit={submit}>
      <p className="access-eyebrow">FORMULA AE</p>
      <h1>ログイン</h1>
      <p>管理者から案内された利用者名とパスワードを入力してください。</p>
      <label>利用者名<input autoComplete="username" required value={username} onChange={event => setUsername(event.target.value)} /></label>
      <label>パスワード
        <PasswordInput autoComplete="current-password" required value={password} onChange={event => setPassword(event.target.value)} />
      </label>
      {error && <p className="access-error" role="alert">{error}</p>}
      <button disabled={busy}>{busy ? "確認しています…" : "ログイン"}</button>
    </form>
  </main>;
}

function PasswordForm({ session, forced, onChanged, onCancel }: {
  session: AccessSession;
  forced?: boolean;
  onChanged: (session: AccessSession) => void;
  onCancel?: () => void;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    if (newPassword !== confirmation) return setError("新しいパスワードが一致しません。");
    try {
      const response = await originalFetch("/api/auth/password", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": session.csrf_token ?? "" },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const value = await jsonOrProblem(response) as unknown as AccessSession;
      setCurrentPassword(""); setNewPassword(""); setConfirmation("");
      onChanged(value);
    } catch (reason) { setError(String(reason)); }
  };
  return <form className="access-form" onSubmit={submit}>
    <h2>{forced ? "最初にパスワードを変更してください" : "パスワード変更"}</h2>
    <p>新しいパスワードは8文字以上にしてください。</p>
    <label>現在のパスワード<PasswordInput autoComplete="current-password" required value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} /></label>
    <label>新しいパスワード<PasswordInput autoComplete="new-password" minLength={8} required value={newPassword} onChange={event => setNewPassword(event.target.value)} /></label>
    <label>新しいパスワード（確認）<PasswordInput autoComplete="new-password" minLength={8} required value={confirmation} onChange={event => setConfirmation(event.target.value)} /></label>
    {error && <p className="access-error" role="alert">{error}</p>}
    <div className="access-actions">{onCancel && <button type="button" className="secondary" onClick={onCancel}>閉じる</button>}<button>変更する</button></div>
  </form>;
}

interface UserDraft {
  username: string;
  password: string;
  role: "user" | "admin";
  priority: number;
}

function AdminPanel({ session, onClose }: { session: AccessSession; onClose: () => void }) {
  const [users, setUsers] = useState<AccessUser[]>([]);
  const [draft, setDraft] = useState<UserDraft>({ username: "", password: "", role: "user", priority: 50 });
  const [reset, setReset] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const headers = useMemo(() => ({ "Content-Type": "application/json", "X-CSRF-Token": session.csrf_token ?? "" }), [session.csrf_token]);
  const load = useCallback(async () => {
    try {
      const response = await originalFetch("/api/admin/users", { cache: "no-store" });
      const value = await jsonOrProblem(response);
      setUsers((value.users ?? []) as AccessUser[]);
      setError("");
    } catch (reason) { setError(String(reason)); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const create = async (event: FormEvent) => {
    event.preventDefault();
    try {
      const response = await originalFetch("/api/admin/users", {
        method: "POST", headers, body: JSON.stringify({
          ...draft, enabled: true, must_change_password: true,
        }),
      });
      await jsonOrProblem(response);
      setDraft({ username: "", password: "", role: "user", priority: 50 });
      await load();
    } catch (reason) { setError(String(reason)); }
  };
  const patch = async (user: AccessUser, change: Partial<AccessUser>) => {
    try {
      const response = await originalFetch(`/api/admin/users/${encodeURIComponent(user.id)}`, {
        method: "PATCH", headers, body: JSON.stringify(change),
      });
      await jsonOrProblem(response); await load();
    } catch (reason) { setError(String(reason)); }
  };
  const remove = async (user: AccessUser) => {
    if (!window.confirm(`${user.username} を削除しますか？`)) return;
    try {
      const response = await originalFetch(`/api/admin/users/${encodeURIComponent(user.id)}`, { method: "DELETE", headers });
      if (!response.ok) await jsonOrProblem(response);
      await load();
    } catch (reason) { setError(String(reason)); }
  };
  const resetPassword = async (user: AccessUser) => {
    const password = reset[user.id] ?? "";
    if (password.length < 8) return setError("再設定するパスワードは8文字以上にしてください。");
    try {
      const response = await originalFetch(`/api/admin/users/${encodeURIComponent(user.id)}/password`, {
        method: "POST", headers, body: JSON.stringify({ new_password: password, must_change_password: true }),
      });
      if (!response.ok) await jsonOrProblem(response);
      setReset(value => ({ ...value, [user.id]: "" }));
      await load();
    } catch (reason) { setError(String(reason)); }
  };
  return <div className="access-dialog-backdrop"><section className="access-dialog admin-dialog" role="dialog" aria-modal="true" aria-label="利用者管理">
    <header><div><p className="access-eyebrow">ADMINISTRATION</p><h2>利用者管理</h2></div><button className="secondary" onClick={onClose}>閉じる</button></header>
    {error && <p className="access-error" role="alert">{error}</p>}
    <form className="admin-create" onSubmit={create}>
      <label>利用者名<input required value={draft.username} onChange={event => setDraft({ ...draft, username: event.target.value })} /></label>
      <label>初期パスワード<PasswordInput minLength={8} required value={draft.password} onChange={event => setDraft({ ...draft, password: event.target.value })} /></label>
      <label>役割<select value={draft.role} onChange={event => setDraft({ ...draft, role: event.target.value as UserDraft["role"] })}><option value="user">一般利用者</option><option value="admin">管理者</option></select></label>
      <label>閲覧優先度<input type="number" min="0" max="100" required value={draft.priority} onChange={event => setDraft({ ...draft, priority: Number(event.target.value) })} /></label>
      <button>追加</button>
    </form>
    <div className="admin-users">
      {users.map(user => <article key={user.id}>
        <div className="admin-user-main"><input aria-label={`${user.username} の利用者名`} defaultValue={user.username} onBlur={event => {
          const username = event.currentTarget.value.trim();
          if (username && username !== user.username) void patch(user, { username });
        }} /><small>ID: {user.id}</small></div>
        <label>役割<select value={user.role} onChange={event => void patch(user, { role: event.target.value as AccessUser["role"] })}><option value="user">一般利用者</option><option value="admin">管理者</option></select></label>
        <label>優先度<input type="number" min="0" max="100" value={user.priority} onChange={event => void patch(user, { priority: Number(event.target.value) })} /></label>
        <label className="admin-enabled"><input type="checkbox" checked={user.enabled} onChange={event => void patch(user, { enabled: event.target.checked })} />有効</label>
        <div className="admin-reset"><PasswordInput minLength={8} placeholder="新しいパスワード" value={reset[user.id] ?? ""} onChange={event => setReset(value => ({ ...value, [user.id]: event.target.value }))} /><button className="secondary" onClick={() => void resetPassword(user)}>再設定</button></div>
        <button className="danger" onClick={() => void remove(user)}>削除</button>
      </article>)}
    </div>
  </section></div>;
}

function AccountBar({ session, onSession, onLogout }: {
  session: AccessSession;
  onSession: (value: AccessSession) => void;
  onLogout: () => void;
}) {
  const [panel, setPanel] = useState<"password" | "admin" | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const barRef = useRef<HTMLElement>(null);
  const user = session.user!;
  useEffect(() => {
    if (!menuOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!barRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("pointerdown", closeOutside);
    window.addEventListener("keydown", closeWithEscape);
    return () => {
      window.removeEventListener("pointerdown", closeOutside);
      window.removeEventListener("keydown", closeWithEscape);
    };
  }, [menuOpen]);
  const openPanel = (next: "password" | "admin") => {
    setMenuOpen(false);
    setPanel(next);
  };
  return <>
    <aside ref={barRef} className="access-account-bar" data-menu-open={menuOpen ? "true" : "false"}>
      <button
        type="button"
        className="access-account-menu-toggle"
        aria-expanded={menuOpen}
        aria-controls="access-account-menu-content"
        onClick={() => setMenuOpen(open => !open)}
      >アカウント</button>
      <div id="access-account-menu-content" className="access-account-menu-content">
        <span><strong>{user.username}</strong> · 優先度 {user.priority}</span>
        <button onClick={() => openPanel("password")}>パスワード変更</button>
        {user.role === "admin" && <button onClick={() => openPanel("admin")}>利用者管理</button>}
        <button onClick={onLogout}>ログアウト</button>
      </div>
    </aside>
    {panel === "password" && <div className="access-dialog-backdrop"><section className="access-dialog" role="dialog" aria-modal="true"><PasswordForm session={session} onChanged={value => { onSession(value); setPanel(null); }} onCancel={() => setPanel(null)} /></section></div>}
    {panel === "admin" && <AdminPanel session={session} onClose={() => setPanel(null)} />}
  </>;
}

function Waiting({ state, onRetry }: { state: ViewingState; onRetry: () => void }) {
  return <main className="access-page"><section className="access-card access-waiting" aria-live="polite">
    <div className="access-spinner" aria-hidden="true" />
    <p className="access-eyebrow">WAITING FOR A VIEWING SLOT</p>
    <h1>回線が混雑しています</h1>
    <p>視聴できる人数の上限に達しています。空きができると自動的に再生画面へ戻ります。</p>
    {state.queue_position && <strong>待機順: {state.queue_position}番</strong>}
    <button onClick={onRetry}>状態を確認</button>
  </section></main>;
}

export function AccessGate({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<"loading" | "open" | "managed" | "error">("loading");
  const [session, setSession] = useState<AccessSession | null>(null);
  const [viewing, setViewing] = useState<ViewingState | null>(null);
  const sessionRef = useRef(session);
  const viewingRef = useRef(viewing);
  const acquirePromiseRef = useRef<Promise<boolean> | null>(null);
  const interactiveRequestRef = useRef(false);
  sessionRef.current = session;
  viewingRef.current = viewing;
  const updateViewing = useCallback((next: ViewingState | null) => {
    if (!next || next.state === "idle") interactiveRequestRef.current = false;
    publishViewingState(next?.state ?? "idle");
    setViewing(next);
  }, []);
  const acquire = useCallback(async (interactive = false): Promise<boolean> => {
    if (interactive) interactiveRequestRef.current = true;
    const current = sessionRef.current;
    if (!current?.authenticated) return false;
    if (viewingRef.current?.state === "active") return true;
    if (acquirePromiseRef.current) return acquirePromiseRef.current;
    const pending = (async () => {
      const response = await originalFetch("/api/viewing/acquire", {
        method: "POST", headers: { "Content-Type": "application/json", "X-CSRF-Token": current.csrf_token ?? "" },
        body: JSON.stringify({ viewer_id: viewerId() }),
      });
      if (response.status === 401) {
        setSession({ schema_version: "fixed-point-access-session/v1", authenticated: false });
        updateViewing(null);
        return false;
      }
      const next = await jsonOrProblem(response) as unknown as ViewingState;
      updateViewing(next);
      return next.state === "active";
    })().finally(() => { acquirePromiseRef.current = null; });
    acquirePromiseRef.current = pending;
    return pending;
  }, [updateViewing]);
  useEffect(() => {
    originalFetch("/api/auth/session", { cache: "no-store" }).then(async response => {
      if (response.status === 404) {
        managedAccess = false; publishViewingState("active"); setMode("open"); return;
      }
      const value = await jsonOrProblem(response) as unknown as AccessSession;
      managedAccess = true; publishViewingState("idle"); setMode("managed"); setSession(value);
    }).catch(() => { managedAccess = false; publishViewingState("idle"); setMode("error"); });
  }, []);
  useEffect(() => {
    const requestInteractiveViewing = () => acquire(true);
    viewingAccessRequester = requestInteractiveViewing;
    return () => {
      if (viewingAccessRequester === requestInteractiveViewing) viewingAccessRequester = null;
    };
  }, [acquire]);
  useEffect(() => {
    if (mode !== "managed" || !session?.authenticated || session.user?.must_change_password) return;
    void acquire(false);
  }, [acquire, mode, session]);
  useEffect(() => {
    if (mode !== "managed" || !session?.authenticated || session.user?.must_change_password) return;
    const playStateChanged = (event: Event) => {
      if ((event as CustomEvent<{ playState?: string }>).detail?.playState === "playing") void acquire(true);
    };
    const engagementChanged = (event: Event) => {
      if ((event as CustomEvent<{ engaged?: boolean }>).detail?.engaged) void acquire(true);
    };
    window.addEventListener("fixed-point-viewing-play-state", playStateChanged);
    window.addEventListener("fixed-point-viewing-conversation-engagement", engagementChanged);
    return () => {
      window.removeEventListener("fixed-point-viewing-play-state", playStateChanged);
      window.removeEventListener("fixed-point-viewing-conversation-engagement", engagementChanged);
    };
  }, [acquire, mode, session?.authenticated, session?.user?.must_change_password]);
  useEffect(() => {
    if (viewing?.state !== "waiting" || interactiveRequestRef.current || !session?.csrf_token) return;
    void originalFetch("/api/viewing/release", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": session.csrf_token },
      body: JSON.stringify({ viewer_id: viewerId() }),
    }).then(response => {
      if (response.ok) updateViewing({
        schema_version: "fixed-point-viewing-state/v1", state: "idle", viewer_id: viewerId(),
        max_viewers: viewing.max_viewers, heartbeat_interval_ms: viewing.heartbeat_interval_ms,
      });
    });
  }, [session?.csrf_token, updateViewing, viewing]);
  useEffect(() => {
    if (mode !== "managed" || !session?.authenticated || session.user?.must_change_password) return;
    const source = new EventSource(`/api/viewing/events?viewer_id=${encodeURIComponent(viewerId())}`);
    const receive = (event: MessageEvent<string>) => updateViewing(JSON.parse(event.data) as ViewingState);
    source.addEventListener("viewing", receive as EventListener);
    source.addEventListener("error", () => {
      void originalFetch("/api/auth/session", { cache: "no-store" }).then(async response => {
        if (!response.ok) return;
        const value = await response.json() as AccessSession;
        if (!value.authenticated) { setSession(value); updateViewing(null); }
      });
    });
    return () => source.close();
  }, [mode, session?.authenticated, session?.user?.must_change_password, updateViewing]);
  useEffect(() => {
    if (viewing?.state !== "active" || !viewing.lease_id || !session?.csrf_token) return;
    const heartbeat = () => { void originalFetch("/api/viewing/heartbeat", {
      method: "POST", headers: { "Content-Type": "application/json", "X-CSRF-Token": session.csrf_token ?? "" },
      body: JSON.stringify({ viewer_id: viewerId(), lease_id: viewing.lease_id }),
    }).then(response => {
      if (response.status === 401) {
        setSession({ schema_version: "fixed-point-access-session/v1", authenticated: false });
        updateViewing(null);
      }
    }); };
    const timer = window.setInterval(heartbeat, viewing.heartbeat_interval_ms);
    return () => window.clearInterval(timer);
  }, [session?.csrf_token, updateViewing, viewing]);
  useEffect(() => {
    if (viewing?.state !== "active" || !viewing.lease_id || !session?.csrf_token) return;
    const release = () => { void originalFetch("/api/viewing/release", {
      method: "POST", keepalive: true,
      headers: { "Content-Type": "application/json", "X-CSRF-Token": session.csrf_token ?? "" },
      body: JSON.stringify({ viewer_id: viewerId(), lease_id: viewing.lease_id }),
    }); };
    window.addEventListener("pagehide", release);
    return () => window.removeEventListener("pagehide", release);
  }, [session?.csrf_token, viewing]);
  useEffect(() => {
    if (viewing?.state !== "active" || !viewing.lease_id || !session?.csrf_token) return;
    let pauseTimer: number | null = null;
    let preparationTimer: number | null = null;
    let preparingVideo: HTMLVideoElement | null = null;
    const shell = document.querySelector<HTMLElement>('[data-testid="stage2-race-shell"]');
    let latestPlayState: string | null = shell?.dataset.playState ?? null;
    let conversationEngaged = shell?.dataset.conversationEngaged === "true";
    let released = false;
    const clearPauseTimer = () => {
      if (pauseTimer !== null) window.clearTimeout(pauseTimer);
      pauseTimer = null;
    };
    const clearMediaReadinessListeners = () => {
      preparingVideo?.removeEventListener("loadeddata", mediaReady);
      preparingVideo?.removeEventListener("canplay", mediaReady);
    };
    const clearPreparationWait = () => {
      clearMediaReadinessListeners();
      if (preparationTimer !== null) window.clearTimeout(preparationTimer);
      preparationTimer = null;
      preparingVideo?.removeEventListener("playing", playbackStarted);
      preparingVideo?.removeEventListener("pause", playbackPaused);
      preparingVideo = null;
    };
    const clearPendingRelease = () => {
      clearPauseTimer();
      clearPreparationWait();
    };
    const releaseViewing = async () => {
      if (released) return;
      released = true;
      clearPendingRelease();
      const response = await originalFetch("/api/viewing/release", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": session.csrf_token ?? "" },
        body: JSON.stringify({ viewer_id: viewerId(), lease_id: viewing.lease_id }),
      });
      if (response.ok) updateViewing({
        schema_version: "fixed-point-viewing-state/v1", state: "idle", viewer_id: viewerId(),
        max_viewers: viewing.max_viewers, heartbeat_interval_ms: viewing.heartbeat_interval_ms,
      });
    };
    const startPausedReleaseTimer = () => {
      clearPauseTimer();
      if (conversationEngaged) return;
      pauseTimer = window.setTimeout(() => void releaseViewing(), PAUSED_VIEWING_RELEASE_DELAY_MS);
    };
    function mediaReady() {
      if (latestPlayState !== "paused" || !preparingVideo
        || preparingVideo.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
      clearMediaReadinessListeners();
      startPausedReleaseTimer();
    }
    function playbackStarted() {
      clearPauseTimer();
      clearMediaReadinessListeners();
    }
    function playbackPaused() {
      if (latestPlayState !== "paused" || !preparingVideo) return;
      if (preparingVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        startPausedReleaseTimer();
        return;
      }
      preparingVideo.addEventListener("loadeddata", mediaReady);
      preparingVideo.addEventListener("canplay", mediaReady);
    }
    const playStateChanged = (event: Event) => {
      const playState = (event as CustomEvent<{ playState?: string }>).detail?.playState;
      latestPlayState = playState ?? null;
      clearPendingRelease();
      if (playState !== "paused" || conversationEngaged) return;
      const video = document.querySelector<HTMLVideoElement>('[data-testid="onboard-video"]');
      preparingVideo = video;
      preparingVideo?.addEventListener("playing", playbackStarted);
      preparingVideo?.addEventListener("pause", playbackPaused);
      if (video && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        startPausedReleaseTimer();
        return;
      }
      preparingVideo?.addEventListener("loadeddata", mediaReady);
      preparingVideo?.addEventListener("canplay", mediaReady);
      preparationTimer = window.setTimeout(() => void releaseViewing(), MEDIA_PREPARATION_GRACE_MS);
    };
    const engagementChanged = (event: Event) => {
      conversationEngaged = Boolean((event as CustomEvent<{ engaged?: boolean }>).detail?.engaged);
      clearPendingRelease();
      if (!conversationEngaged && latestPlayState === "paused") {
        playStateChanged(new CustomEvent("fixed-point-viewing-play-state", {
          detail: { playState: latestPlayState },
        }));
      }
    };
    window.addEventListener("fixed-point-viewing-play-state", playStateChanged);
    window.addEventListener("fixed-point-viewing-conversation-engagement", engagementChanged);
    if (latestPlayState === "paused" && !conversationEngaged) {
      playStateChanged(new CustomEvent("fixed-point-viewing-play-state", {
        detail: { playState: latestPlayState },
      }));
    }
    return () => {
      window.removeEventListener("fixed-point-viewing-play-state", playStateChanged);
      window.removeEventListener("fixed-point-viewing-conversation-engagement", engagementChanged);
      clearPendingRelease();
    };
  }, [session?.csrf_token, updateViewing, viewing]);
  const logout = async () => {
    if (!session?.csrf_token) return;
    await originalFetch("/api/auth/logout", { method: "POST", headers: { "X-CSRF-Token": session.csrf_token } });
    updateViewing(null); setSession({ schema_version: "fixed-point-access-session/v1", authenticated: false });
  };
  if (mode === "loading") return <main className="access-page"><section className="access-card"><p>再生環境を確認しています…</p></section></main>;
  if (mode === "error") return <main className="access-page"><section className="access-card"><h1>再生環境を確認できません</h1><p>通信状態を確認してからページを再読み込みしてください。</p><button onClick={() => window.location.reload()}>再読み込み</button></section></main>;
  if (mode === "open") return <>{children}</>;
  if (!session?.authenticated) return <Login onLogin={setSession} />;
  if (session.user?.must_change_password) return <main className="access-page"><section className="access-card"><PasswordForm forced session={session} onChanged={setSession} /><button className="secondary" onClick={() => void logout()}>ログアウト</button></section></main>;
  if (viewing?.state === "waiting" && interactiveRequestRef.current) return <><AccountBar session={session} onSession={setSession} onLogout={() => void logout()} /><Waiting state={viewing} onRetry={() => void acquire(true)} /></>;
  return <><AccountBar session={session} onSession={setSession} onLogout={() => void logout()} />{children}</>;
}
